import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { SkillInvocation } from "~/api/conversationTypes";
import { useCreateDocument, useUpdateDocument } from "~/api/queries";
import type { Document } from "~/api/types";
import { useApplyToDocument, writeBlockedReason } from "~/features/conversations/documentWrite";
import type { DocumentPorts } from "~/features/conversations/ports/editorPorts";
import { McpCallTrace } from "~/features/conversations/skills/McpCallTrace";
import { parseMcpCall, skillMode, skillTitle } from "~/features/conversations/skills/parseSkills";
import { Button } from "~/foundation/components/Button";
import { Text } from "~/foundation/components/Text";
import { cn } from "~/foundation/utils/cn";

/** The four write modes, in the words a person can act on. */
const MODE_LABEL: Record<string, string> = {
  append: "Append to end",
  insert: "Insert at cursor",
  replace_document: "Replace document",
  replace_selection: "Replace selection",
};

/**
 * One skill invocation, rendered (W8 §B.6 / S7).
 *
 * The distinction that matters is the descriptor's `kind`, and it is not cosmetic:
 *
 *  - `document-write` / `document-create` / `document-rename` are **proposals**. The server
 *    has NOT done them. They get an approve-before-apply card, and the button is the moment
 *    a person accepts responsibility for the text.
 *  - `read` skills already RAN, server-side, before this message existed. They get a compact
 *    trace and **never an Apply button** — offering one would imply the reader could undo or
 *    re-run something that has already happened.
 *
 * An unknown skill id renders as unsupported rather than as nothing, so a server that learns
 * a skill before the client does produces a visible gap instead of a silent one.
 */
export function SkillCard({
  invocation,
  document,
  ports,
  agent,
}: {
  invocation: SkillInvocation;
  document: Document;
  ports: DocumentPorts;
  agent: string;
}) {
  const { t } = useTranslation();
  const { apply, busy } = useApplyToDocument();
  const createDocument = useCreateDocument();
  const updateDocument = useUpdateDocument();
  const [state, setState] = useState<"idle" | "applied" | "dismissed">("idle");
  const [notice, setNotice] = useState<string | null>(null);
  const [working, setWorking] = useState(false);

  const descriptor = invocation.descriptor;

  if (!descriptor) {
    return (
      <div className="rounded-md border border-divider border-dashed p-2">
        <Text variant="small" className="text-text-tertiary">
          {t("This skill isn't supported here")} · {invocation.skillId}
        </Text>
      </div>
    );
  }

  const header = (
    <header className="flex items-center gap-2">
      <span aria-hidden>{descriptor.icon}</span>
      <Text variant="small" className="font-medium text-text">
        {t(descriptor.label)}
      </Text>
      {descriptor.kind === "document-write" ? (
        <span className="rounded-sm bg-bg-secondary px-1.5 py-0.5 text-caption text-text-tertiary">
          {t(MODE_LABEL[skillMode(invocation)] ?? "Append to end")}
        </span>
      ) : null}
      {state === "dismissed" ? (
        <span className="ms-auto text-caption text-text-tertiary">{t("Dismissed")}</span>
      ) : state === "applied" ? (
        <span className="ms-auto text-caption text-success">{t("Applied")}</span>
      ) : null}
    </header>
  );

  // A read skill is a receipt, not an offer.
  if (descriptor.kind === "read") {
    const isMcp = invocation.skillId === "call_mcp_tool";
    return (
      <div className="flex flex-col gap-1.5 rounded-md border border-divider p-2">
        {header}
        {isMcp ? (
          <McpCallTrace call={parseMcpCall(invocation.body)} />
        ) : (
          <pre
            dir="ltr"
            className="scrollbar-thin max-h-24 overflow-auto whitespace-pre-wrap rounded-sm bg-bg-secondary p-2 text-caption text-text-secondary"
          >
            {invocation.body || invocation.params.query || ""}
          </pre>
        )}
      </div>
    );
  }

  const blocked = writeBlockedReason(document, ports, t);
  const mode = skillMode(invocation);
  const title = skillTitle(invocation, t("Untitled"));

  const run = async () => {
    setNotice(null);
    setWorking(true);
    try {
      if (descriptor.kind === "document-create") {
        // kms had to hand `documents.create` ProseMirror JSON, which is why the skill needed
        // a `parseMarkdown` port at all. Slate's document API is markdown-in, so the body goes
        // straight through and this path works on BOTH lanes, ports or no ports.
        await createDocument.mutateAsync({
          title,
          text: invocation.body,
          ...(document.collectionId ? { collectionId: document.collectionId } : {}),
        });
        setState("applied");
        setNotice(t("Created “{{title}}”", { title }));
        return;
      }
      if (descriptor.kind === "document-rename") {
        const next = invocation.params.title?.trim();
        if (!next) {
          setNotice(t("No new title provided"));
          return;
        }
        await updateDocument.mutateAsync({ id: document.id, data: { title: next } });
        setState("applied");
        setNotice(t("Renamed to “{{title}}”", { title: next }));
        return;
      }
      const outcome = await apply({ document, markdown: invocation.body, mode, ports, agent });
      if (outcome.ok) {
        setState("applied");
        setNotice(t("Written to the document"));
      } else {
        setNotice(outcome.reason);
      }
    } catch (error) {
      setNotice(error instanceof Error ? error.message : t("Couldn't write to the document"));
    } finally {
      setWorking(false);
    }
  };

  return (
    <div
      className={cn(
        "flex flex-col gap-1.5 rounded-md border border-divider p-2",
        state === "dismissed" ? "opacity-60" : null,
      )}
    >
      {header}
      <pre
        dir="ltr"
        className="scrollbar-thin max-h-50 overflow-auto whitespace-pre-wrap rounded-sm bg-bg-secondary p-2 text-caption text-text-secondary"
      >
        {invocation.body}
      </pre>
      {state === "idle" ? (
        <div className="flex flex-wrap items-center gap-1">
          <Button
            size="sm"
            variant="primary"
            disabled={Boolean(blocked) || busy || working}
            onClick={() => void run()}
          >
            {descriptor.kind === "document-create"
              ? t("Create document")
              : descriptor.kind === "document-rename"
                ? t("Rename")
                : t("Apply to document")}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setState("dismissed")}>
            {t("Dismiss")}
          </Button>
          {/* The honest absence, written out: a disabled button carries no tooltip. */}
          {blocked ? (
            <Text variant="small" className="text-text-tertiary">
              {blocked}
            </Text>
          ) : null}
        </div>
      ) : null}
      <div aria-live="polite" className="min-h-4 text-caption text-text-tertiary">
        {notice}
      </div>
    </div>
  );
}
